import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Loader2, Mail, Calendar, Clock, MapPin, CheckCircle, 
  XCircle, LogOut, ChevronRight, Bike, AlertCircle
} from "lucide-react";

const StatusBadge = ({ status }) => {
  const styles = {
    pending: { bg: "bg-amber-100 border-amber-200", text: "text-amber-800", label: "Pending" },
    confirmed: { bg: "bg-green-100 border-green-200", text: "text-green-800", label: "Confirmed" },
    cancelled: { bg: "bg-red-100 border-red-200", text: "text-red-700", label: "Cancelled" },
  };
  const s = styles[status] || styles.pending;
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${s.bg} ${s.text}`}>{s.label}</span>;
};

function BookingCard({ booking }) {
  const isPast = new Date(booking.datetime_iso) < new Date();
  const isDual = booking.bike_choice === "Trishaw and Wheelchair Bike";

  return (
    <div className={`bg-white rounded-xl border ${isPast ? 'border-slate-200 opacity-75' : 'border-green-200'} p-4 mb-3`} data-testid={`requester-booking-${booking.id}`}>
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-4 h-4 text-green-600" />
            <span className="font-semibold text-slate-800">{booking.date_formatted}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600 text-sm mb-2">
            <Clock className="w-4 h-4" />
            <span>{booking.duration_hours}hr</span>
          </div>
          {booking.meeting_point && (
            <div className="flex items-center gap-2 text-slate-600 text-sm mb-2">
              <MapPin className="w-4 h-4" />
              <span>{booking.meeting_point}</span>
            </div>
          )}
          {isDual ? (
            <div className="text-slate-500 text-sm mt-2 space-y-1">
              <div className="flex items-center gap-2">
                <Bike className="w-4 h-4" />
                <span>Trishaw: {booking.trishaw_volunteer || <span className="text-amber-600">Awaiting volunteer</span>}</span>
              </div>
              <div className="flex items-center gap-2">
                <Bike className="w-4 h-4" />
                <span>Wheelchair Bike: {booking.wheelchair_volunteer || <span className="text-amber-600">Awaiting volunteer</span>}</span>
              </div>
            </div>
          ) : booking.volunteer_name ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm mt-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span>Volunteer: {booking.volunteer_name}</span>
            </div>
          ) : booking.status === "pending" ? (
            <div className="flex items-center gap-2 text-amber-600 text-sm mt-2">
              <AlertCircle className="w-4 h-4" />
              <span>Awaiting volunteer confirmation</span>
            </div>
          ) : null}
        </div>
        <StatusBadge status={booking.status} />
      </div>
    </div>
  );
}

export default function RequesterPortal() {
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState("loading"); // loading, login, verify, portal
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [bookings, setBookings] = useState({ upcoming: [], past: [] });
  const [requesterEmail, setRequesterEmail] = useState("");
  const [activeTab, setActiveTab] = useState("upcoming");
  const [loadingBookings, setLoadingBookings] = useState(false);

  // Check for token in URL or localStorage
  useEffect(() => {
    const urlToken = searchParams.get("token");
    const storedToken = localStorage.getItem("requester_token");

    if (urlToken) {
      // Direct access token from booking confirmation or email
      localStorage.setItem("requester_token", urlToken);
      // Validate by fetching bookings
      fetchBookings(urlToken);
    } else if (storedToken) {
      fetchBookings(storedToken);
    } else {
      // Check for verify token (magic link login)
      const verifyToken = searchParams.get("token");
      if (searchParams.toString().includes("verify")) {
        setPage("login");
      } else {
        setPage("login");
      }
    }
  }, []);

  const fetchBookings = (token) => {
    setLoadingBookings(true);
    localStorage.setItem("requester_token", token);
    api.requesterGetBookings()
      .then((r) => {
        setBookings({ upcoming: r.data.upcoming, past: r.data.past });
        setRequesterEmail(r.data.email);
        setPage("portal");
      })
      .catch(() => {
        localStorage.removeItem("requester_token");
        setPage("login");
      })
      .finally(() => setLoadingBookings(false));
  };

  const handleRequestLogin = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await api.requesterRequestLogin(email.trim());
      toast.success("If you have bookings with us, you'll receive a login link shortly. Check your email.");
      setPage("sent");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("requester_token");
    setRequesterEmail("");
    setBookings({ upcoming: [], past: [] });
    setPage("login");
  };

  // Loading state
  if (page === "loading") {
    return (
      <div className="min-h-screen bg-[#fcfdfc] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-green-700" />
      </div>
    );
  }

  // Login page
  if (page === "login" || page === "sent") {
    return (
      <div className="min-h-screen bg-[#fcfdfc] flex items-center justify-center px-4" style={{ fontFamily: "'Public Sans', sans-serif" }}>
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <img src="/favicon.png" alt="DVC" className="w-12 h-12" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>My Bookings</h1>
            <p className="text-slate-500 text-sm mt-1">Dodder Valley Cycling</p>
          </div>

          {page === "sent" ? (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center" data-testid="login-sent">
              <Mail className="w-10 h-10 text-green-600 mx-auto mb-3" />
              <h2 className="font-semibold text-slate-800 mb-2">Check your email</h2>
              <p className="text-slate-600 text-sm">We've sent a login link to <strong>{email}</strong>. Click the link in the email to view your bookings.</p>
              <button onClick={() => setPage("login")} className="mt-4 text-green-700 text-sm font-medium hover:underline">
                Try a different email
              </button>
            </div>
          ) : (
            <form onSubmit={handleRequestLogin} className="bg-white rounded-2xl border border-green-100 shadow-sm p-6" data-testid="requester-login-form">
              <p className="text-slate-600 text-sm mb-4">Enter the email address you used when making your booking.</p>
              <div className="relative mb-4">
                <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="pl-10 h-11"
                  data-testid="requester-email-input"
                />
              </div>
              <Button type="submit" disabled={sending} className="w-full h-11 bg-[#15803d] hover:bg-green-800 text-white rounded-full font-semibold" data-testid="requester-login-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
                {sending ? "Sending..." : "Send Login Link"}
              </Button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Portal
  return (
    <div className="min-h-screen bg-[#fcfdfc]" style={{ fontFamily: "'Public Sans', sans-serif" }}>
      {/* Header */}
      <div className="bg-[#15803d] text-white">
        <div className="max-w-2xl mx-auto px-4 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/favicon.png" alt="DVC" className="w-6 h-6" />
            <span className="font-semibold">My Bookings</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-green-200 text-sm hidden sm:inline">{requesterEmail}</span>
            <button onClick={handleLogout} className="text-green-200 hover:text-white transition-colors" data-testid="requester-logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {loadingBookings ? (
          <div className="text-center py-16"><Loader2 className="w-8 h-8 animate-spin text-green-700 mx-auto" /></div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex gap-2 mb-5">
              {[
                { key: "upcoming", label: "Upcoming", count: bookings.upcoming.length },
                { key: "past", label: "Past", count: bookings.past.length },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeTab === tab.key ? "bg-[#15803d] text-white" : "bg-white border border-green-200 text-slate-600 hover:bg-green-50"
                  }`}
                  data-testid={`requester-tab-${tab.key}`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>

            {/* Booking list */}
            {activeTab === "upcoming" && (
              <div data-testid="requester-upcoming-list">
                {bookings.upcoming.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No upcoming bookings</p>
                  </div>
                ) : (
                  bookings.upcoming.map((b) => <BookingCard key={b.id} booking={b} />)
                )}
              </div>
            )}

            {activeTab === "past" && (
              <div data-testid="requester-past-list">
                {bookings.past.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No past bookings</p>
                  </div>
                ) : (
                  bookings.past.map((b) => <BookingCard key={b.id} booking={b} />)
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
